import { useState } from 'react'

import { Box, Card, Icon, Text, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import {
  CreateProjectTaskMutationVariables,
  ProjectWorkerForSelectFragment,
  SubprojectForSelectFragment,
  UpdateProjectTaskMutationVariables,
} from 'generated/resources/graphql'

import DayPicker from 'modules/core/components/DayPicker'
import SelectField from 'modules/core/components/SelectField'
import TextEditor from 'modules/core/components/TextEditor'
import TextField from 'modules/core/components/TextField'
import { dateBackendToUI, dateUIToBackend, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { taskStatuses } from 'modules/tasks/lib/status'

import { ProjectWorkerSelector } from '../selectors/ProjectWorkerSelector'
import { SubprojectSelector } from '../selectors/SubprojectSelector'
import NoEmployeeAssignationSelectInfo from './NoEmployeeAssignationSelectInfo'

interface ProjectUser {
  value: number
  label: string
  url: string
}

interface Props<T> {
  task: T
  initialFiles: File[]
  projectUsers?: ProjectUser[]
  onChange: (approvalFlow: T) => void
  onFilesChange: (files: File[]) => void
  onSubprojectChange?: (subproject: SubprojectForSelectFragment | null) => void
  onProjectWorkersChange?: (projectWorkers: ProjectWorkerForSelectFragment[]) => void
  hasProjectWorkers?: boolean
}

export const ProjectTaskForm = <
  T extends CreateProjectTaskMutationVariables | UpdateProjectTaskMutationVariables
>({
  task,
  initialFiles,
  onChange,
  onFilesChange,
  onSubprojectChange,
  onProjectWorkersChange,
  hasProjectWorkers,
}: Props<T>) => {
  const statusOptions = taskStatuses().map((status) => {
    return {
      label: status.title,
      value: status.status,
    }
  })

  const [selectedSubproject, setSelectedSubproject] = useState<SubprojectForSelectFragment | null>(
    null
  )
  const [selectedProjectWorkers, setSelectedProjectWorkers] = useState<
    ProjectWorkerForSelectFragment[]
  >([])

  return (
    <>
      <Box paddingBottom="s16">
        <TextField
          value={task.name}
          label={i18n.t('project_management.tasks.form.labels.title')}
          onChange={(value) => onChange({ ...task, name: value })}
        ></TextField>
      </Box>
      <Box flexDirection="row" gap="s16" paddingBottom="s16">
        <SelectField
          label={i18n.t('project_management.tasks.form.labels.status')}
          options={statusOptions}
          onChange={(value) => onChange({ ...task, status: value })}
          value={task.status}
        />
        <SubprojectSelector
          projectId={task.projectId}
          value={selectedSubproject}
          onChange={(value) => {
            onSubprojectChange?.(value)
            setSelectedSubproject(value)
            onChange({ ...task, subprojectId: value?.id })
          }}
        />
      </Box>
      <Box paddingBottom="s16">
        {hasProjectWorkers ? (
          <ProjectWorkerSelector
            label={i18n.t('project_management.tasks.form.labels.assignees')}
            projectId={task.projectId}
            value={selectedProjectWorkers}
            onChange={(value) => {
              onProjectWorkersChange?.(value)
              setSelectedProjectWorkers(value)
              onChange({ ...task, assigneeIds: value.map((worker) => worker.employee?.access?.id) })
            }}
          />
        ) : (
          <NoEmployeeAssignationSelectInfo />
        )}
      </Box>
      <Box paddingBottom="s16" gap="s8">
        <Text.Normal size="200" weight="medium" color="grey1000">
          {i18n.t('project_management.tasks.form.labels.description')}
        </Text.Normal>
        <TextEditor
          placeholder={i18n.t('project_management.tasks.form.labels.description')}
          onFiles={onFilesChange}
          onChange={(data) => {
            if (data && typeof data === 'string') {
              onChange({ ...task, content: data })
            }
            if (data && typeof data !== 'string') {
              onChange({ ...task, content: data.value })
            }
          }}
          initialEditorState={{ files: initialFiles, data: task.content || '' }}
          height="s200"
        />
      </Box>
      <Card>
        <Card.Content>
          <Box gap="s12">
            <Text.Normal size="200" weight="medium" color="grey1000">
              {i18n.t('project_management.tasks.form.labels.dates')}
            </Text.Normal>
            <Text.Normal size="300" weight="normal" color="grey800">
              {i18n.t('project_management.tasks.form.labels.dates_description')}
            </Text.Normal>
          </Box>
          <Box paddingTop="s12" flexDirection="row" gap="s8">
            <DayPicker
              withNull
              label={i18n.t('project_management.tasks.form.labels.start_date')}
              onChange={(value) => onChange({ ...task, startsOn: dateUIToBackend(value) })}
              value={task.startsOn ? dateBackendToUI(task.startsOn) : ''}
              maxDate={task.dueOn ? stringToDate(dateBackendToUI(task.dueOn), 'ui') : null}
            />
            <DayPicker
              withNull
              label={i18n.t('project_management.tasks.form.labels.due_date')}
              onChange={(value) => onChange({ ...task, dueOn: dateUIToBackend(value) })}
              value={task.dueOn ? dateBackendToUI(task.dueOn) : ''}
              minDate={task.startsOn ? stringToDate(dateBackendToUI(task.startsOn), 'ui') : null}
            />
          </Box>
          <Box flexDirection="row" gap="s8" paddingTop="s12">
            <Icon.Large icon="bell" color="grey800" />
            <Text.Normal size="200" weight="normal" color="grey800">
              {i18n.t('project_management.tasks.form.labels.dates_hint')}
            </Text.Normal>
          </Box>
        </Card.Content>
      </Card>
      <Box gap="s8">
        <Box paddingTop="s16">
          <Card>
            <Card.Content>
              <Box
                flexDirection="row"
                justifyContent="spaceBetween"
                width="full"
                alignItems="center"
              >
                <Text.Normal size="300" weight="medium" color="grey1000">
                  {i18n.t('project_management.tasks.form.labels.follow_up')}
                </Text.Normal>
                <TogglerCheckbox
                  value={task.followUp ?? false}
                  onChange={(value) => onChange({ ...task, followUp: value })}
                />
              </Box>
            </Card.Content>
          </Card>
        </Box>
      </Box>
    </>
  )
}
